import type { DraftState, WizardItemType, WizardSampleGroup } from '../../Model/OrderWizard/OrderWizardDraft.ts'
import type { CSVMappingResult } from '../../View/Page/HomePage/Modals/CSVModalTypes.ts'
import type { ArchiveSample } from './archive.ts'
import { archiveSamples, isArchiveOrder } from './archive.ts'
import { sheetRevision } from './sheet/sheetMapping.ts'
import { rowImport } from './sheet/sheetImport.ts'

export type ArchiveApplyEntry = {
  group: WizardSampleGroup
  itemId: string
  sheetKey: string
  /** First write: every row in one batch on the archive's item, which the classic import turns into one item per row. */
  initial: boolean
  /** The samples this batch writes, in the order of `results`. */
  samples: ArchiveSample[]
  results: CSVMappingResult[]
  /** Samples the sheet added after the archive was split; the classic import cannot add items to it any more. */
  unwritable: string[]
}

/**
 * What each archive should receive from the sheet. The first write sends all of an archive's rows keyed to
 * its one item, as the classic import does: the first row updates the item, every further row becomes a
 * new item on the same files. Later sheet revisions update the items already made.
 */
export const planArchiveApply = (draft: DraftState): ArchiveApplyEntry[] => {
  const sheet = draft.sheet
  if (!sheet || !isArchiveOrder(draft)) {
    return []
  }
  const sheetKey = sheetRevision(sheet)
  const samplesByGroup = archiveSamples(draft)
  return Object.values(draft.groups).flatMap((group): ArchiveApplyEntry[] => {
    const itemId = group.item.itemId
    const samples = samplesByGroup[group.key] ?? []
    if (group.item.status !== 'created' || !itemId || samples.length === 0 || group.applied?.status === 'pending') {
      return []
    }
    if (group.applied?.status === 'done' && group.applied.sheetKey === sheetKey) {
      return []
    }
    const known = group.archiveItems ?? {}
    const initial = Object.keys(known).length === 0
    const written = initial ? samples : samples.filter((sample) => known[sample.key])
    const results = written.map(
      (sample) =>
        rowImport(
          sheet.rows[sample.rowIndex],
          sheet,
          initial ? itemId : known[sample.key].itemId,
          {
            forward: sample.forwardPrimer,
            reverse: sample.reversePrimer
          },
          sample.name
        ).result
    )
    return [
      {
        group,
        itemId,
        sheetKey,
        initial,
        samples: written,
        results,
        unwritable: initial ? [] : samples.filter((sample) => !known[sample.key]).map((sample) => sample.name)
      }
    ]
  })
}

/** Archive items whose server type differs from their sample's label: to convert as the classic view converts. */
export const archiveConversions = (
  draft: DraftState
): { group: WizardSampleGroup; key: string; itemId: string; itemType: WizardItemType }[] => {
  if (!isArchiveOrder(draft)) {
    return []
  }
  const samplesByGroup = archiveSamples(draft)
  return Object.values(draft.groups).flatMap((group) =>
    (samplesByGroup[group.key] ?? []).flatMap((sample) => {
      const item = group.archiveItems?.[sample.key]
      return item && item.itemType && item.itemType !== sample.itemType
        ? [{ group, key: sample.key, itemId: item.itemId, itemType: sample.itemType }]
        : []
    })
  )
}
